/**
 * Everything is open on paper.
 *
 * The diagnostics — what was read, the operator ledger, the quality issues, the
 * artefact hashes — sit behind folds because none of them has to be read to
 * finish the job. On paper that reasoning inverts: a printout is a record, and a
 * record that silently omits the ledger is a record of nothing much. So every
 * fold is opened for the print and put back afterwards.
 *
 * Done here rather than in CSS because it cannot be done in CSS. A closed
 * `<details>` is hidden by the browser with `content-visibility` on an internal
 * slot, which no author rule reaches; `display: revert` on the children looks
 * like it works and does not. The print stylesheet handles everything else.
 */
export function openFoldsWhilePrinting(): void {
  if (typeof window === 'undefined') return;

  let reopened: HTMLDetailsElement[] = [];

  const before = (): void => {
    reopened = [...document.querySelectorAll<HTMLDetailsElement>('details:not([open])')];
    for (const fold of reopened) fold.open = true;
  };

  const after = (): void => {
    for (const fold of reopened) fold.open = false;
    reopened = [];
  };

  window.addEventListener('beforeprint', before);
  window.addEventListener('afterprint', after);

  // Safari fires neither event, but it does match the print media query, so the
  // same pair of moments is reachable through it.
  const printing = window.matchMedia?.('print');
  printing?.addEventListener('change', (event) => (event.matches ? before() : after()));
}
